import { noteNameSchema } from '@tunasong/schemas'
import type { NoteName } from '@tunasong/schemas'
import { getNoteChromaticIndex } from '../note/index.js'
import { ALL_CHORDS } from './all-chords.js'
import type { ChordVariant } from './chord-types.js'
import { getName } from './get-name.js'

/**
 * The Chord Chooser's "Chord name" filter (GH #2488), matched and ranked per Bowie:
 * - only the root letter is case-insensitive: "M" is major and "m" minor, so "am7" is Am7, never AM7;
 * - the typed root must be the chord's root, then the rest is a prefix: "A" finds A chords, not A#;
 * - a query ending in "m" never prefix-matches "maj": "Am" finds Am, Am7, Am9…; "Ama" finds Amaj7;
 * - order: an exact match, then literal prefixes shortest first, then the library's aliases, then enharmonic
 *   roots ("Gb" finds the F# chords); ties keep library order.
 */
const splitRoot = (symbol: string) => {
  const match = /^([A-G][#b]?)(.*)$/.exec(symbol)
  return match ? { root: match[1], rest: match[2] } : undefined
}

function isNoteName(value: string): value is NoteName {
  return noteNameSchema.safeParse(value).success
}

/** "am7" → "Am7": the root letter only */
export const normalizeChordQuery = (query: string) => {
  const trimmed = query.trim()
  return trimmed ? `${trimmed[0].toUpperCase()}${trimmed.slice(1)}` : ''
}

const restMatches = (rest: string, typed: string) =>
  rest.startsWith(typed) && !(typed.endsWith('m') && rest.slice(typed.length).startsWith('aj'))

/** Each canonical chord name's aliases, from the library (ChordBuilder.getAliasNames) */
const aliasesByName = Object.entries(ALL_CHORDS).reduce<Map<string, string[]>>((map, [alias, entry]) => {
  if (entry.isAlias) {
    const name = getName(entry.chord)
    map.set(name, [...(map.get(name) ?? []), alias])
  }
  return map
}, new Map())

const aliasesOf = (chord: ChordVariant) => aliasesByName.get(getName(chord)) ?? []

export const filterChordsByName = (query: string, chords: ChordVariant[]): ChordVariant[] => {
  const typed = normalizeChordQuery(query)
  const parts = splitRoot(typed)
  if (!typed) {
    return chords
  }
  if (!parts) {
    return []
  }
  const order = new Map(chords.map((chord, index) => [chord, index]))
  const byLibraryOrder = (a: ChordVariant, b: ChordVariant) => (order.get(a) ?? 0) - (order.get(b) ?? 0)

  const exact = chords.filter(chord => getName(chord) === typed || aliasesOf(chord).includes(typed))
  const literal = chords
    .filter(chord => chord.root === parts.root && restMatches(getName(chord).slice(chord.root.length), parts.rest))
    .sort((a, b) => getName(a).length - getName(b).length || byLibraryOrder(a, b))
  const aliased = chords.filter(chord =>
    aliasesOf(chord).some(alias => {
      const aliasParts = splitRoot(alias)
      return aliasParts?.root === parts.root && restMatches(aliasParts.rest, parts.rest)
    })
  )
  const typedRoot = parts.root
  const pitchClass = isNoteName(typedRoot) ? getNoteChromaticIndex(typedRoot) : undefined
  const enharmonic = chords
    .filter(
      chord =>
        pitchClass !== undefined &&
        chord.root !== typedRoot &&
        getNoteChromaticIndex(chord.root) === pitchClass &&
        restMatches(getName(chord).slice(chord.root.length), parts.rest)
    )
    .sort((a, b) => getName(a).length - getName(b).length || byLibraryOrder(a, b))

  return [...new Set([...exact, ...literal, ...aliased, ...enharmonic])]
}

/**
 * The chord Enter inserts: the typed spelling when the library knows it ("Gb7" stays Gb7, the user's key
 * context), else an exact match among `chords`.
 */
export const chordForTypedName = (query: string, chords: ChordVariant[]): ChordVariant | undefined => {
  const typed = normalizeChordQuery(query)
  const spelled = ALL_CHORDS[typed]
  if (spelled && !spelled.isAlias) {
    return spelled.chord
  }
  return chords.find(chord => getName(chord) === typed || aliasesOf(chord).includes(typed)) ?? spelled?.chord
}
